import { CombinedGraphQLErrors, CombinedProtocolErrors, ErrorLike } from '@apollo/client';
import { ScrollArea } from '@optools/components/ui/scroll-area';
import { ReactNode } from 'react';
import { ExternalToast, toast } from 'sonner';

export interface ApolloErrorMessageProps {
  description?: ReactNode | (() => React.ReactNode);
  error: ErrorLike;
  title: string;
}

export const ErrorList = ({ children, title }: { children: ReactNode; title: string }) => {
  return (
    <div>
      <h2>{title}</h2>
      <ul className="mx-0 my-1 list-disc space-y-2 pl-4 text-muted-foreground">{children}</ul>
    </div>
  );
};

export const buildGqlErrorMessages = (error: ErrorLike) => {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.map((err, idx) => {
      const { extensions, message } = err;
      const { classification, code, executionId } = extensions ?? {
        classification: undefined,
        code: undefined,
        executionId: undefined,
      };
      return (
        <li key={`gql-error-message-${idx}`}>{`${message}${
          extensions
            ? `${classification ? ` - ${classification}` : ''} ${code ? ` code: ${code}` : ''} ${
                executionId ? ` id: ${executionId}` : ''
              }`
            : ''
        }`}</li>
      );
    });
  }
};

export const buildProtocolErrors = (error: ErrorLike) => {
  if (CombinedProtocolErrors.is(error)) {
    return error.errors.map((err, idx) => {
      const { message } = err;
      return <li key={`gql-error-message-${idx}`}>{message}</li>;
    });
  }
};
export function ApolloErrorMessage({
  description,
  error,
  title = 'Error',
}: ApolloErrorMessageProps) {
  const gqlErrorMessages = buildGqlErrorMessages(error);
  const protocolErrorMessages = buildProtocolErrors(error);

  return (
    <div className="flex h-full w-full gap-2.5">
      <div className="flex w-full flex-col">
        {title ? <h1 className="mb-2 font-bold">{title}</h1> : null}
        {description ? (
          typeof description === 'string' ? (
            <h2 className="mb-2 text-muted-foreground">{description}</h2>
          ) : typeof description === 'function' ? (
            description()
          ) : null
        ) : null}
        <ScrollArea className="max-h-72 w-full">
          {error && !gqlErrorMessages && !protocolErrorMessages ? <p>{error.message}</p> : null}
          {gqlErrorMessages ? (
            <ErrorList title="GraphQL Errors">{gqlErrorMessages}</ErrorList>
          ) : null}
          {protocolErrorMessages ? (
            <ErrorList title="Protocol Errors">{protocolErrorMessages}</ErrorList>
          ) : null}
        </ScrollArea>
      </div>
    </div>
  );
}

type DisplayApolloErrorArgs = { error: ErrorLike; options?: ExternalToast; title: string };

export const displayApolloError = ({ error, options, title }: DisplayApolloErrorArgs) => {
  const { description = '', ...restOfOptions } = options || {};
  return toast.error(<ApolloErrorMessage description={description} error={error} title={title} />, {
    closeButton: true,
    dismissible: true,
    duration: Infinity,
    ...restOfOptions,
  });
};
